import React, { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, FileText, PlusCircle, Save, Send, Trash2, Upload } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isAuthenticated } from "../../auth";
import { applyToJob, updateJobApplication, uploadJobApplicationDocument } from "../../auth/apiJobs";
import { elakayAutoText } from "../dataElakay";
import {
  ACCEPTED_DOCUMENT_ACCEPT,
  ACCEPTED_DOCUMENT_EXTENSIONS,
  APPLICATION_DOCUMENT_TYPES,
  MAX_APPLICANT_SOCIAL_LINKS,
  getFileExtension,
} from "./JobSections";
import "./JobSections.css";
import { toFileUrl } from "../../../utils/fileUrl";

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  location: "",
  resumeUrl: "",
  portfolioUrl: "",
  linkedinUrl: "",
  coverLetter: "",
  documentType: "cv",
  documents: [],
  socialLinks: [],
};

const formFromApplication = (application) => ({
  fullName: application?.fullName || "",
  email: application?.email || "",
  phone: application?.phone || "",
  location: application?.location || "",
  resumeUrl: application?.resumeUrl || "",
  portfolioUrl: application?.portfolioUrl || "",
  linkedinUrl: application?.linkedinUrl || "",
  coverLetter: application?.coverLetter || "",
  documentType: "cv",
  documents: Array.isArray(application?.documents) ? application.documents : [],
  socialLinks: Array.isArray(application?.socialLinks) ? application.socialLinks : [],
});

// Single create/edit apply form for a Job Posting, shared by the public
// job detail page (mode="create", see JobDetailSection in JobSections.jsx)
// and the candidate's self-service edit flow on /my-job-applications
// (mode="edit"). Keeping one component with two modes avoids maintaining
// two divergent copies of the same fields/validation/link-management UI.
const JobApplicationForm = ({ job, mode = "create", application = null, onSuccess }) => {
  const isEdit = mode === "edit";
  const auth = isAuthenticated();
  const [form, setForm] = useState(() => (isEdit ? formFromApplication(application) : emptyForm));
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setForm(formFromApplication(application));
    } else {
      setForm((prev) => ({
        ...prev,
        fullName: auth?.user?.name || auth?.user?.firstName || prev.fullName,
        email: auth?.user?.email || prev.email,
      }));
    }
    setMessage("");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, application?._id, job?._id]);

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const acceptingApplications = isEdit || (job?.isAcceptingApplications !== false && job?.status === "published");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isEdit && job?.applicationMode === "external" && job?.applicationUrl) {
      window.open(job.applicationUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (!form.fullName.trim() || !form.email.trim()) {
      setError(elakayAutoText("h_f9d37222da3621", "Nom complet et email sont requis."));
      return;
    }
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      setError(elakayAutoText("h_jobapply_phoneinvalid", "Le numero de telephone n'est pas valide."));
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        const { documentType, ...payload } = form;
        const updated = await updateJobApplication(application._id, payload, auth?.token);
        setMessage(elakayAutoText("h_myapps_update_success", "Ta candidature a ete mise a jour avec succes."));
        onSuccess?.(updated?.application || updated);
      } else {
        await applyToJob(job._id, form, auth?.token);
        setMessage(elakayAutoText("h_c6e21dbaed5829", "Candidature envoyee. L'equipe Elakay peut maintenant la consulter."));
        setForm((prev) => ({ ...prev, coverLetter: "", documents: [], socialLinks: [] }));
      }
    } catch (err) {
      setError(
        err.message ||
          (isEdit
            ? elakayAutoText("h_myapps_update_error", "Impossible de mettre a jour ta candidature. Verifie les informations et reessaie.")
            : elakayAutoText("h_8e651a0d12923c", "Impossible d'envoyer la candidature."))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const uploadDocument = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const extension = getFileExtension(file.name);
    if (!ACCEPTED_DOCUMENT_EXTENSIONS.includes(extension)) {
      setError(elakayAutoText("h_jobapply_docformat", "Format invalide. Ajoute seulement PDF, DOC ou DOCX."));
      return;
    }

    setUploadingDocument(true);
    setError("");
    setMessage("");
    try {
      const uploaded = await uploadJobApplicationDocument(file, auth?.token);
      const document = {
        title: uploaded.originalName || file.name,
        documentType: form.documentType,
        kind: uploaded.kind || extension.replace(".", ""),
        url: uploaded.url,
        path: uploaded.path || "",
        originalName: uploaded.originalName || file.name,
        filename: uploaded.filename || "",
        size: uploaded.size || file.size,
        uploadedAt: new Date().toISOString(),
      };
      // Replace any existing document of the same type in place (mirrors the
      // backend's mergeApplicantDocuments merge-by-documentType semantics)
      // so a candidate replacing their CV doesn't end up with two entries.
      setForm((prev) => ({
        ...prev,
        documents: [
          ...(prev.documents || []).filter((existing) => existing.documentType !== form.documentType),
          document,
        ],
      }));
      setMessage(elakayAutoText("h_jobapply_docadded", "Document ajoute a la candidature."));
    } catch (err) {
      setError(err.message || elakayAutoText("h_99b8a53b5726d8", "Impossible d'uploader ce document."));
    } finally {
      setUploadingDocument(false);
    }
  };

  const removeDocument = (index) => {
    setForm((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter((_, documentIndex) => documentIndex !== index),
    }));
  };

  const addSocialLink = () => {
    setForm((prev) => {
      const current = Array.isArray(prev.socialLinks) ? prev.socialLinks : [];
      if (current.length >= MAX_APPLICANT_SOCIAL_LINKS) return prev;
      return { ...prev, socialLinks: [...current, { platform: "", url: "" }] };
    });
  };

  const updateSocialLink = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      socialLinks: (prev.socialLinks || []).map((link, linkIndex) =>
        linkIndex === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const removeSocialLink = (index) => {
    setForm((prev) => ({
      ...prev,
      socialLinks: (prev.socialLinks || []).filter((_, linkIndex) => linkIndex !== index),
    }));
  };

  const allowLinks = Boolean(job?.allowApplicantSocialLinks);
  const requireLinks = Boolean(job?.requireApplicantSocialLinks);

  return (
    <form className="job-detail-apply-form" onSubmit={handleSubmit}>
      {!isEdit && !acceptingApplications && (
        <div className="job-detail-message error">
          {elakayAutoText("h_09a8c1de0bd797", "Ce poste n'accepte pas de candidature actuellement.")}
        </div>
      )}
      <input value={form.fullName} onChange={setField("fullName")} placeholder={elakayAutoText("h_e58e9ba9e12326", "Nom complet")} />
      <input value={form.email} onChange={setField("email")} placeholder={elakayAutoText("h_84add5b2952787", "Email")} type="email" disabled={isEdit} />
      <PhoneInput
        className="job-detail-apply-phone"
        international
        countrySelectProps={{ unicodeFlags: true }}
        value={form.phone}
        onChange={(val) => setForm((prev) => ({ ...prev, phone: val || "" }))}
        placeholder={elakayAutoText("h_fbadb22cc438ab", "Telephone")}
      />
      {form.phone && !isValidPhoneNumber(form.phone) && (
        <span className="job-detail-apply-field-hint">
          {elakayAutoText("h_jobapply_phoneinvalid", "Le numero de telephone n'est pas valide.")}
        </span>
      )}
      <input value={form.location} onChange={setField("location")} placeholder={elakayAutoText("h_2e2a4e52678cce", "Ville / pays")} />
      <input value={form.resumeUrl} onChange={setField("resumeUrl")} placeholder={elakayAutoText("h_77fc36f9a9525e", "Lien CV ou resume")} />
      <input value={form.portfolioUrl} onChange={setField("portfolioUrl")} placeholder={elakayAutoText("h_bb9ef866568dc5", "Portfolio / site")} />
      <input value={form.linkedinUrl} onChange={setField("linkedinUrl")} placeholder={elakayAutoText("h_6b6390a4416131", "LinkedIn")} />

      {allowLinks && (
        <div className="job-detail-apply-social">
          <div className="job-detail-apply-social-header">
            <span>{elakayAutoText("h_job_social_links_label", "Reseaux sociaux")}</span>
            <button
              type="button"
              className="job-detail-apply-social-add"
              onClick={addSocialLink}
              disabled={(form.socialLinks || []).length >= MAX_APPLICANT_SOCIAL_LINKS}
            >
              <PlusCircle size={14} />
              {elakayAutoText("h_job_social_links_add", "Ajouter un lien")}
            </button>
          </div>
          {requireLinks && (
            <p className="job-detail-apply-field-hint">
              {elakayAutoText("h_job_social_links_required", "Ce poste exige au moins un lien (portfolio, chaine, reseau social...).")}
            </p>
          )}
          {(form.socialLinks || []).map((link, index) => (
            <div className="job-detail-apply-social-row" key={index}>
              <input
                value={link.platform}
                onChange={(event) => updateSocialLink(index, "platform", event.target.value)}
                placeholder={elakayAutoText("h_job_social_links_platform", "Titre (ex: Youtube, Mon portfolio)")}
              />
              <input
                value={link.url}
                onChange={(event) => updateSocialLink(index, "url", event.target.value)}
                placeholder={elakayAutoText("h_job_social_links_url", "Lien (ex: https://...)")}
              />
              <button
                type="button"
                onClick={() => removeSocialLink(index)}
                aria-label={elakayAutoText("h_54ec24a19c2152", "Retirer")}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <textarea value={form.coverLetter} onChange={setField("coverLetter")} placeholder={elakayAutoText("h_39cd7fc6843107", "Message de candidature")} />

      <select value={form.documentType} onChange={setField("documentType")}>
        {APPLICATION_DOCUMENT_TYPES.map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <label className="job-detail-apply-upload">
        <Upload size={16} />
        <span>
          {uploadingDocument
            ? elakayAutoText("h_5b436218392ae9", "Upload en cours...")
            : elakayAutoText("h_jobapply_choosefile", "Ajouter un document (PDF, DOC, DOCX)")}
        </span>
        <input
          type="file"
          accept={ACCEPTED_DOCUMENT_ACCEPT}
          onChange={uploadDocument}
          disabled={uploadingDocument}
        />
      </label>
      {form.documents.length > 0 && (
        <div className="job-detail-apply-documents">
          {form.documents.map((document, index) => (
            <div className="job-detail-apply-document" key={`${document.url}-${index}`}>
              <span><FileText size={14} />{document.title}</span>
              <div className="job-detail-apply-document-actions">
                <a href={toFileUrl(document.url)} target="_blank" rel="noreferrer" aria-label={elakayAutoText("h_42c07747756067", "Ouvrir")}>
                  <ExternalLink size={14} />
                </a>
                <button type="button" onClick={() => removeDocument(index)} aria-label={elakayAutoText("h_54ec24a19c2152", "Retirer")}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button type="submit" disabled={submitting || (!isEdit && !acceptingApplications)}>
        {isEdit ? <Save size={18} /> : job?.applicationMode === "external" && job?.applicationUrl ? <ExternalLink size={18} /> : <Send size={18} />}
        {submitting
          ? elakayAutoText("h_5a377e56fb25e1", "Envoi...")
          : isEdit
            ? elakayAutoText("h_myapps_save", "Enregistrer les modifications")
            : elakayAutoText("h_0152b213be3162", "Envoyer ma candidature")}
      </button>
      {!isEdit && job?.applicationUrl && (
        <a className="job-detail-apply-secondary" href={job.applicationUrl} target="_blank" rel="noreferrer">
          <ExternalLink size={16} />{elakayAutoText("h_38a67b609c66e9", "Lien externe")}
        </a>
      )}
      {message && <div className="job-detail-message success">{message}</div>}
      {error && <div className="job-detail-message error">{error}</div>}
      {!isEdit && (
        <p className="job-detail-apply-note">
          <CheckCircle2 size={14} />
          {elakayAutoText("h_68c65edb5cb7b5", "Elakay examine les candidatures en fonction des besoins actifs de l'equipe.")}
        </p>
      )}
    </form>
  );
};

export default JobApplicationForm;
